import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  hockeyPositions,
  stickHands,
  type HockeyPosition,
  type HockeyProfileView,
  type StickHand,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { hockeyPositionLabelKeys, stickHandLabelKeys, togglePosition } from './hockeyProfile';

export interface HockeyProfileSectionProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}` */
  base: string;
  csrfToken: string;
  athleteId: string;
  canManage: boolean;
}

type ErrorKey = ApiRequestError['messageKey'];

/** Preferred positions and stick hand; everyone who sees the athlete reads it, managers change it. */
export function HockeyProfileSection({
  locale,
  base,
  csrfToken,
  athleteId,
  canManage,
}: HockeyProfileSectionProps): React.JSX.Element {
  const path = `${base}/athletes/${athleteId}/hockey-profile`;
  const [profile, setProfile] = useState<HockeyProfileView | null>(null);
  const [positions, setPositions] = useState<HockeyPosition[]>([]);
  const [stickHand, setStickHand] = useState<StickHand | ''>('');
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const loaded = await apiRequest<HockeyProfileView>(path);
      setProfile(loaded);
      setPositions(loaded.preferredPositions);
      setStickHand(loaded.stickHand ?? '');
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }, [path]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(): Promise<void> {
    if (profile === null) return;
    setErrorKey(null);
    setSaved(false);
    try {
      const updated = await apiRequest<HockeyProfileView>(path, {
        method: 'PUT',
        csrfToken,
        ifMatchVersion: profile.version,
        body: { preferredPositions: positions, stickHand: stickHand === '' ? null : stickHand },
      });
      setProfile(updated);
      setSaved(true);
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography component="h4" variant="subtitle2">
        {translate(locale, 'hockeyProfile.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ my: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ my: 1 }}>
          {translate(locale, 'hockeyProfile.saved')}
        </Alert>
      )}
      {profile !== null && !canManage && (
        <Typography color="text.secondary">
          {translate(locale, 'hockeyProfile.positions')}:{' '}
          {profile.preferredPositions.length === 0
            ? translate(locale, 'hockeyProfile.none')
            : profile.preferredPositions
                .map((position) => translate(locale, hockeyPositionLabelKeys[position]))
                .join(', ')}
          {' · '}
          {translate(locale, 'hockeyProfile.stickHand')}:{' '}
          {profile.stickHand === null
            ? translate(locale, 'hockeyProfile.none')
            : translate(locale, stickHandLabelKeys[profile.stickHand])}
        </Typography>
      )}
      {profile !== null && canManage && (
        <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
          <Box role="group" aria-label={translate(locale, 'hockeyProfile.positions')}>
            {hockeyPositions.map((position) => (
              <FormControlLabel
                key={position}
                label={translate(locale, hockeyPositionLabelKeys[position])}
                control={
                  <Checkbox
                    checked={positions.includes(position)}
                    onChange={(event) => {
                      setSaved(false);
                      setPositions((current) =>
                        togglePosition(current, position, event.target.checked),
                      );
                    }}
                  />
                }
              />
            ))}
          </Box>
          <TextField
            select
            size="small"
            sx={{ minWidth: 200 }}
            label={translate(locale, 'hockeyProfile.stickHand')}
            value={stickHand}
            onChange={(event) => {
              setSaved(false);
              setStickHand(event.target.value as StickHand | '');
            }}
          >
            <MenuItem value="">{translate(locale, 'hockeyProfile.none')}</MenuItem>
            {stickHands.map((hand) => (
              <MenuItem key={hand} value={hand}>
                {translate(locale, stickHandLabelKeys[hand])}
              </MenuItem>
            ))}
          </TextField>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              void save();
            }}
          >
            {translate(locale, 'hockeyProfile.save')}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
